import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUser, SignIn } from "@clerk/clerk-react";
import { joinLeague } from "../services/leagueService";
import { apiPost, apiGet } from "../services/api";
import PageHeader from "../components/PageHeader";
import AlertMessage from "../components/AlertMessage";

function JoinLeaguePage() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [league, setLeague] = useState(null);
  const [joining, setJoining] = useState(false);

  // Fetch league details on mount
  useEffect(() => {
    if (!inviteCode) return;

    const controller = new AbortController();
    let isMounted = true;

    async function fetchLeague() {
      try {
        const leagueData = await apiGet(
          `/Leagues/validate/${inviteCode}`,
          { signal: controller.signal }
        );
        if (isMounted) {
          setLeague(leagueData);
        }
      } catch (err) {
        if (err?.name === "AbortError" || !isMounted) return;
        if (isMounted) {
          setError("Invalid invite code or league not found.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchLeague();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [inviteCode]);

  const handleJoinLeague = async () => {
    if (!user) {
      setError("Please sign in first.");
      return;
    }

    setJoining(true);
    setError("");

    const controller = new AbortController();

    try {
      // Sync user
      const syncData = await apiPost(
        "/Users/sync",
        {
          clerkUserId: user.id,
          email: user.primaryEmailAddress?.emailAddress || "",
        },
        { signal: controller.signal }
      );

      const resolvedUserId = syncData?.id ?? syncData?.userId ?? syncData?.appUserId ?? null;

      if (!Number.isInteger(resolvedUserId) || resolvedUserId <= 0) {
        throw new Error("Failed to sync user");
      }

      // Join league
      await joinLeague(inviteCode, resolvedUserId, { signal: controller.signal });

      // Success - redirect
      navigate("/leagues", { state: { message: `Successfully joined ${league?.name}!` } });
    } catch (err) {
      if (err?.name === "AbortError") return;

      let errorMessage = "Failed to join league.";
      if (err.message?.includes("not found")) {
        errorMessage = "League not found.";
      } else if (err.message?.includes("already")) {
        errorMessage = "You're already a member of this league.";
      }

      setError(errorMessage);
    } finally {
      setJoining(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="space-y-6">
        <div className="h-32 animate-pulse rounded-3xl bg-slate-200" />
        <div className="h-40 animate-pulse rounded-3xl bg-white shadow-sm ring-1 ring-slate-200" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Join League"
          title="Loading..."
          subtitle="Please wait"
          dark
        />
        <div className="rounded-3xl bg-white p-6 sm:p-8 shadow-sm ring-1 ring-slate-200">
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <div className="animate-spin">
              <svg
                className="h-8 w-8 text-emerald-600"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !league) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Join League"
          title="League Not Found"
          subtitle="The invite code is invalid or expired"
          dark
        />
        <div className="rounded-3xl bg-white p-6 sm:p-8 shadow-sm ring-1 ring-slate-200">
          <AlertMessage message={error} type="error" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Join League"
        title={league?.name || "Join League"}
        subtitle={league ? `${league.tournamentType === "WorldCup" ? "🏆 World Cup" : "⚽ Premier League"} • ${league.memberCount} member${league.memberCount !== 1 ? "s" : ""}` : ""}
        dark
      />

      {error && (
        <AlertMessage message={error} type="error" />
      )}

      {!user ? (
        <div className="space-y-6">
          <div className="rounded-3xl bg-white p-6 sm:p-8 shadow-sm ring-1 ring-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Sign in to join this league
            </h2>
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 mb-6">
              <p className="text-sm font-medium text-slate-900">{league?.name}</p>
              <p className="text-xs text-slate-600 mt-1">{league?.memberCount} member{league?.memberCount !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <div className="rounded-3xl bg-white p-6 sm:p-8 shadow-sm ring-1 ring-slate-200">
            <SignIn routing="hash" redirectUrl={window.location.href} />
          </div>
        </div>
      ) : (
        <div className="rounded-3xl bg-white p-6 sm:p-8 shadow-sm ring-1 ring-slate-200">
          <div className="space-y-4">
            <p className="text-slate-600">Ready to join this league?</p>
            <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
              <p className="text-sm font-medium text-emerald-900">{league?.name}</p>
              <p className="text-xs text-emerald-600 mt-1">{league?.memberCount} member{league?.memberCount !== 1 ? "s" : ""}</p>
            </div>
            <button
              onClick={handleJoinLeague}
              disabled={joining}
              className="w-full inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {joining ? "Joining..." : "Join League"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default JoinLeaguePage;
